import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { TablaDatos, ayudanteColumnas } from '../../componentes/TablaDatos'
import type { BonoCatalogo } from './catalogo'
import { formatearFecha } from './formato'
import { InsigniaMotivo } from './insignias'
import type { FilaTabla } from './tabla'
import { etiquetaDias, resumenHabitual } from './tabla'

/**
 * La tabla de clientes con casillas. Es la pantalla: se elige el dia, se ven los
 * clientes de ese dia, se marcan los que toca y se publica.
 *
 * Tres decisiones que vienen de mirar los datos de verdad:
 *
 * - **La columna de pedido habitual esta vacia mas de la mitad de las veces.**
 *   31 de cada 50 clientes no tienen pedido previo en Odoo. Sale en gris, con el
 *   motivo escrito, y **no impide marcar la fila**: lo que hace falta es
 *   elegirle el bono, y ese desplegable esta ahi mismo.
 * - **El bono se elige, no se escribe.** El desplegable ofrece el catalogo de
 *   Odoo; el backend vuelve a buscar el producto por nombre al componer el
 *   pedido y esa busqueda falla si el nombre casa con varios o con ninguno.
 * - **Las rutas se ensenan tal cual.** 119 clientes no tienen ninguna y 75
 *   tienen las cinco. Ni se les inventa un dia ni se les esconde.
 */

const ayudante = ayudanteColumnas<FilaTabla>()

interface Props {
  filas: FilaTabla[]
  bonos: BonoCatalogo[]
  /** El catalogo todavia no ha llegado: el desplegable no ofrece nada aun. */
  catalogoPendiente: boolean
  marcados: ReadonlySet<string>
  /** El bono de esa fila: el corregido a mano, o el que trajo el historico. */
  bonoDeFila: (fila: FilaTabla) => BonoCatalogo | null
  onAlternar: (fila: FilaTabla) => void
  onMarcarVisibles: (marcar: boolean) => void
  onElegirBono: (contactId: string, productoId: string) => void
  /** Abre el panel lateral de reparto de ese cliente (rediseño B.3). */
  onAbrirFila: (fila: FilaTabla) => void
  /** contact_id de la fila con el panel abierto, para resaltarla. */
  filaAbierta: string | null
}

/**
 * Lo que hay dentro de una fila y no es texto -la casilla, el desplegable- se
 * blinda contra la propagacion: `TablaDatos` hace clicable la fila entera para
 * que marcarla sea facil, y sin esto elegir un bono marcaria tambien la casilla.
 */
function SinPropagar({ children }: { children: ReactNode }) {
  return (
    <span
      onClick={(evento) => evento.stopPropagation()}
      onKeyDown={(evento) => evento.stopPropagation()}
    >
      {children}
    </span>
  )
}

function Casilla({
  etiqueta,
  marcada,
  indeterminada = false,
  onCambiar,
}: {
  etiqueta: string
  marcada: boolean
  indeterminada?: boolean
  onCambiar: (marcada: boolean) => void
}) {
  return (
    <SinPropagar>
      <input
        type="checkbox"
        aria-label={etiqueta}
        checked={marcada}
        ref={(nodo) => {
          // El estado intermedio no se puede poner por atributo: solo existe
          // como propiedad del nodo.
          if (nodo) nodo.indeterminate = indeterminada && !marcada
        }}
        onChange={(evento) => onCambiar(evento.target.checked)}
        className="size-4 cursor-pointer accent-acento"
      />
    </SinPropagar>
  )
}

/**
 * El pedido de siempre de ese cliente. Cuando no lo hay dice **por que**: sin
 * pedidos previos, sin ficha o sin NIF no se corrigen igual, y ninguno de los
 * tres es un fallo de la pantalla.
 */
function CeldaHabitual({ fila }: { fila: FilaTabla }) {
  const pedido = fila.pedido_habitual
  const resumen = resumenHabitual(pedido)

  return (
    <div className="flex flex-col">
      <span className={pedido.encontrado ? 'text-tinta' : 'text-tinta-apagada'}>{resumen}</span>
      {pedido.encontrado && pedido.fecha ? (
        <span className="text-xs text-tinta-apagada">Desde el {formatearFecha(pedido.fecha)}</span>
      ) : null}
    </div>
  )
}

export function TablaClientes({
  filas,
  bonos,
  catalogoPendiente,
  marcados,
  bonoDeFila,
  onAlternar,
  onMarcarVisibles,
  onElegirBono,
  onAbrirFila,
  filaAbierta,
}: Props) {
  const visiblesMarcadas = filas.filter((fila) => marcados.has(fila.cliente.contact_id)).length
  const todasMarcadas = filas.length > 0 && visiblesMarcadas === filas.length

  const columnas = useMemo(
    () =>
      ayudante.columns([
        ayudante.display({
          id: 'marcar',
          header: () => (
            <Casilla
              etiqueta="Marcar todos los visibles"
              marcada={todasMarcadas}
              indeterminada={visiblesMarcadas > 0}
              onCambiar={onMarcarVisibles}
            />
          ),
          cell: (info) => (
            <Casilla
              etiqueta={`Marcar ${info.row.original.cliente.nombre}`}
              marcada={marcados.has(info.row.original.cliente.contact_id)}
              onCambiar={() => onAlternar(info.row.original)}
            />
          ),
        }),
        ayudante.accessor((fila) => fila.cliente.nombre, {
          id: 'nombre',
          header: 'Cliente',
          cell: (info) => <span className="font-medium text-tinta">{info.getValue()}</span>,
        }),
        ayudante.accessor((fila) => fila.cliente.code, {
          id: 'nif',
          header: 'NIF',
          cell: (info) => info.getValue() || 'Sin NIF',
        }),
        ayudante.accessor((fila) => etiquetaDias(fila.cliente.rutas), {
          id: 'rutas',
          header: 'Día de ruta',
          cell: (info) => (
            <span className={info.row.original.cliente.rutas?.length ? undefined : 'text-tinta-apagada'}>
              {info.getValue()}
            </span>
          ),
        }),
        ayudante.accessor((fila) => fila.cliente.ciudad, {
          id: 'ciudad',
          header: 'Población',
          cell: (info) => info.getValue() || '-',
        }),
        ayudante.accessor((fila) => resumenHabitual(fila.pedido_habitual), {
          id: 'habitual',
          header: 'Reparto',
          cell: (info) => {
            const fila = info.row.original
            const bono = bonoDeFila(fila)
            const abierta = filaAbierta === fila.cliente.contact_id
            return (
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col gap-0.5">
                  <CeldaHabitual fila={fila} />
                  <span className={bono ? 'text-xs font-medium text-acento-tinta' : 'text-xs text-tinta-apagada'}>
                    {bono ? `Bono ${bono.unidades} · ${bono.litraje}L` : 'Sin bono elegido'}
                  </span>
                </div>
                <SinPropagar>
                  <button
                    type="button"
                    onClick={() => onAbrirFila(fila)}
                    aria-label={`Configurar el reparto de ${fila.cliente.nombre}`}
                    className={[
                      'shrink-0 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                      abierta
                        ? 'border-acento bg-acento-suave text-acento-tinta'
                        : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
                    ].join(' ')}
                  >
                    {abierta ? 'Editando' : 'Configurar'}
                  </button>
                </SinPropagar>
              </div>
            )
          },
        }),
        ayudante.accessor((fila) => fila.motivo, {
          id: 'ficha',
          header: 'Ficha del cliente',
          cell: (info) => <InsigniaMotivo motivo={info.getValue()} />,
        }),
      ]),
    [
      bonos,
      bonoDeFila,
      catalogoPendiente,
      marcados,
      onAlternar,
      onElegirBono,
      onMarcarVisibles,
      onAbrirFila,
      filaAbierta,
      todasMarcadas,
      visiblesMarcadas,
    ],
  )

  return (
    <>
      {/* Tabla en escritorio. Siete columnas a 375 px no se leen. */}
      <div className="hidden lg:block">
        <TablaDatos
          datos={filas}
          columnas={columnas}
          estadoVacio={null}
          etiqueta="Clientes del reparto"
          obtenerId={(fila) => fila.cliente.contact_id}
          onSeleccionar={onAlternar}
        />
      </div>

      {/* Tarjetas apiladas en movil, con lo mismo que dice la tabla. El
          "marcar todos" vive en la cabecera de la tabla, que aqui no existe:
          sin este, marcar cincuenta clientes a 375 px serian cincuenta gestos. */}
      <div className="flex flex-col gap-2 lg:hidden">
        <div className="flex items-center gap-2 rounded-lg border border-borde bg-superficie px-3 py-2">
          <Casilla
            etiqueta="Marcar todos los visibles"
            marcada={todasMarcadas}
            indeterminada={visiblesMarcadas > 0}
            onCambiar={onMarcarVisibles}
          />
          <span className="text-sm text-tinta-2">Marcar todos los visibles</span>
        </div>

        <ul aria-label="Clientes en tarjetas" className="flex flex-col gap-2">
          {filas.map((fila) => (
            <li
              key={fila.cliente.contact_id}
              className="flex flex-col gap-2 rounded-xl border border-borde bg-superficie px-4 py-3"
            >
              {/* `flex-wrap`: en pantallas estrechas la insignia baja de linea
                  antes que dejar el nombre en una columna de 80 px. */}
              <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                <Casilla
                  etiqueta={`Marcar ${fila.cliente.nombre}`}
                  marcada={marcados.has(fila.cliente.contact_id)}
                  onCambiar={() => onAlternar(fila)}
                />
                <div className="flex min-w-40 flex-1 basis-40 flex-col">
                  <span className="text-sm font-medium text-tinta">{fila.cliente.nombre}</span>
                  <span className="text-xs text-tinta-apagada">
                    {fila.cliente.code || 'Sin NIF'} - {etiquetaDias(fila.cliente.rutas)}
                    {fila.cliente.ciudad ? ` - ${fila.cliente.ciudad}` : ''}
                  </span>
                </div>
                <InsigniaMotivo motivo={fila.motivo} />
              </div>
              <CeldaHabitual fila={fila} />
              <div className="flex items-center justify-between gap-2">
                <span className={bonoDeFila(fila) ? 'text-xs font-medium text-acento-tinta' : 'text-xs text-tinta-apagada'}>
                  {bonoDeFila(fila)
                    ? `Bono ${bonoDeFila(fila)!.unidades} · ${bonoDeFila(fila)!.litraje}L`
                    : 'Sin bono elegido'}
                </span>
                <button
                  type="button"
                  onClick={() => onAbrirFila(fila)}
                  className={[
                    'shrink-0 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                    filaAbierta === fila.cliente.contact_id
                      ? 'border-acento bg-acento-suave text-acento-tinta'
                      : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
                  ].join(' ')}
                >
                  {filaAbierta === fila.cliente.contact_id ? 'Editando' : 'Configurar'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}
